import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { fetchApplicationType, updateApplicationType, fetchJobs } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function ApplicationTypeEditPage() {
  const { businessDomainId, applicationTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [form, setForm] = useState({ applicationTypeName: "", applicationTypeDescription: "", url: "" });
  const [fullData, setFullData] = useState(null);

  useEffect(() => {
    (async () => {
      try { setJobs(await fetchJobs(businessDomainId)); }
      catch (e) { /* jobs may be empty */ }
    })();
  }, [businessDomainId]);

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchApplicationType(applicationTypeId);
        setFullData(d);
        setForm({
          applicationTypeName: d.applicationTypeName || "",
          applicationTypeDescription: d.applicationTypeDescription || "",
          url: d.url || "",
        });
        // Pre-select job if already set
        if (d.jobId) {
          setSelectedJob({ jobId: d.jobId, jobName: d.jobName || "" });
        }
      } catch (e) { notify.apiError(e); }
    })();
  }, [applicationTypeId]);

  // Sync selectedJob with the jobs list once both are loaded
  useEffect(() => {
    if (jobs.length > 0 && fullData?.jobId) {
      const matched = jobs.find((j) => j.jobId === fullData.jobId);
      if (matched) setSelectedJob(matched);
    }
  }, [jobs, fullData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...fullData,
        ...form,
        jobId: selectedJob ? selectedJob.jobId : null,
        jobName: selectedJob ? selectedJob.jobName : null,
      };
      await updateApplicationType(payload);
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/application-types/${applicationTypeId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("applicationType.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required multiline label={t("applicationType.name")}
              name="applicationTypeName" value={form.applicationTypeName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("applicationType.description")}
              name="applicationTypeDescription" value={form.applicationTypeDescription} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("applicationType.job")}</InputLabel>
              <Select
                value={selectedJob}
                label={t("applicationType.job")}
                onChange={(e) => setSelectedJob(e.target.value)}
              >
                {jobs.map((job) => (
                  <MenuItem key={job.jobId} value={job}>
                    {job.jobName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth label={t("common.url")}
              name="url" value={form.url} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/application-types/${applicationTypeId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
